import { useState } from 'react';
import type { ChangeEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Ship, ShipPlan } from '@clan-heart/shared';
import { SHIPS, rateLabel } from '@clan-heart/shared';
import { deleteShipPlan, getShipPlans, putShipPlan, uploadFlag } from '../api.js';
import { Button } from '../components/Button.js';
import styles from './ShipsPage.module.css';

const MAX_PLAN_BYTES = 2 * 1024 * 1024;

function groupByRate(ships: readonly Ship[]): Map<number, Ship[]> {
  const byRate = new Map<number, Ship[]>();
  for (const ship of ships) {
    const list = byRate.get(ship.rate) ?? [];
    list.push(ship);
    byRate.set(ship.rate, list);
  }
  return byRate;
}

function ShipPlanRow({ ship, plan }: { ship: Ship; plan: ShipPlan | undefined }) {
  const queryClient = useQueryClient();
  const [credit, setCredit] = useState(plan?.credit ?? '');
  const [error, setError] = useState<string | null>(null);

  function invalidate() {
    queryClient.invalidateQueries({ queryKey: ['ships', 'plans'] });
  }

  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      const uploaded = await uploadFlag(file);
      return putShipPlan(ship.id, { image: uploaded.flag, credit });
    },
    onSuccess: invalidate,
    onError: (err) => setError(err instanceof Error ? err.message : 'Upload failed.'),
  });

  const creditMutation = useMutation({
    mutationFn: () => {
      if (!plan) throw new Error('Upload a plan before setting its credit.');
      return putShipPlan(ship.id, { image: plan.image, credit });
    },
    onSuccess: invalidate,
    onError: (err) => setError(err instanceof Error ? err.message : 'Something went wrong.'),
  });

  const removeMutation = useMutation({
    mutationFn: () => deleteShipPlan(ship.id),
    onSuccess: invalidate,
    onError: (err) => setError(err instanceof Error ? err.message : 'Something went wrong.'),
  });

  function handleFileChange(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = ''; // lets the same file be picked again after an error
    if (!file) return;
    setError(null);
    if (file.size > MAX_PLAN_BYTES) {
      setError('Image must be 2 MB or smaller.');
      return;
    }
    uploadMutation.mutate(file);
  }

  function handleCreditBlur() {
    if (plan && credit !== plan.credit) creditMutation.mutate();
  }

  return (
    <article className={styles.row}>
      <div className={styles.rowTop}>
        <div className={styles.shipInfo}>
          <span className={styles.shipName}>{ship.name}</span>
          <span className={styles.meta}>
            {ship.decks} decks · {ship.guns} guns
          </span>
        </div>
        {plan ? (
          <img className={styles.thumb} src={plan.image} alt="" />
        ) : (
          <span className={styles.noPlan}>No plan yet</span>
        )}
      </div>
      <div className={styles.field}>
        <label htmlFor={`credit-${ship.id}`}>Credit</label>
        <input
          id={`credit-${ship.id}`}
          value={credit}
          onChange={(e) => setCredit(e.target.value)}
          onBlur={handleCreditBlur}
          placeholder="e.g. National Maritime Museum"
        />
      </div>
      {error && <p className={styles.error}>{error}</p>}
      <div className={styles.actions}>
        <label className={styles.uploadLabel}>
          {uploadMutation.isPending ? 'Uploading…' : plan ? 'Replace plan' : 'Upload plan'}
          <input
            type="file"
            accept="image/png,image/webp,image/jpeg"
            className={styles.uploadInput}
            onChange={handleFileChange}
            disabled={uploadMutation.isPending}
          />
        </label>
        {plan && (
          <Button
            type="button"
            variant="danger"
            disabled={removeMutation.isPending}
            onClick={() => removeMutation.mutate()}
          >
            Remove plan
          </Button>
        )}
      </div>
    </article>
  );
}

export function ShipsPage() {
  const query = useQuery({ queryKey: ['ships', 'plans'], queryFn: getShipPlans });
  const plans = query.data?.plans ?? [];
  const plansByShip = new Map(plans.map((p) => [p.shipId, p]));
  const byRate = groupByRate(SHIPS);
  const rates = [...byRate.keys()].sort((a, b) => a - b);

  return (
    <section>
      <h2 className={styles.heading}>Ship plans</h2>
      {query.isLoading && <p>Loading…</p>}
      {query.isError && <p className={styles.error}>{(query.error as Error).message}</p>}
      {rates.map((rate) => (
        <div key={rate} className={styles.sectionBlock}>
          <h3 className={styles.subheading}>{rateLabel(rate)}</h3>
          <div className={styles.list}>
            {byRate.get(rate)!.map((ship) => (
              <ShipPlanRow key={ship.id} ship={ship} plan={plansByShip.get(ship.id)} />
            ))}
          </div>
        </div>
      ))}
    </section>
  );
}
